import {
  KnowledgeOwnerType,
  KnowledgePageMeta,
  KnowledgeSpace,
} from '@this+that/models';
import log from 'loglevel';
import { StateCreator } from 'zustand';

import { collectDescendantPageIds } from '../../../hooks/Brain/Knowledge/Page/utils/pageTreeUtils';
import { useTeamStore } from '../../Team/useTeamStore';
import {
  BrainSidebarSlice,
  BrainSpaceGroupKey,
  BrainStore,
  INITIAL_BRAIN_STATE,
  MoveSubtreeBetweenSpacesInput,
  PendingPageLocation,
} from '../types';

// A WebSocket broadcast or optimistic object never carries the full field
// set — merging only defined keys keeps an echo from erasing what it didn't load.
function mergeDefinedFields<T extends object>(
  base: T,
  incoming: Partial<T>,
): T {
  const definedEntries = (
    Object.entries(incoming) as [keyof T, T[keyof T] | undefined][]
  ).filter(([, value]) => value !== undefined);
  return { ...base, ...Object.fromEntries(definedEntries) } as T;
}

function pickNonEmptyPages(
  ...candidates: (KnowledgePageMeta[] | undefined)[]
): KnowledgePageMeta[] {
  const nonEmpty = candidates.find(
    (candidate) => candidate && candidate.length > 0,
  );
  return nonEmpty ?? [];
}

// oldEntry and duplicate can each independently be the one still holding a
// loaded page tree, so pages falls back across both regardless of which one
// mergeDefinedFields used as its field base.
function mergeReplacedSpace(
  oldEntry: KnowledgeSpace | undefined,
  duplicate: KnowledgeSpace | undefined,
  incoming: KnowledgeSpace,
): KnowledgeSpace {
  const fieldBase = oldEntry ?? duplicate;
  return {
    ...(fieldBase ? mergeDefinedFields(fieldBase, incoming) : incoming),
    pages: pickNonEmptyPages(incoming.pages, oldEntry?.pages, duplicate?.pages),
  };
}

function appendSpaceIfAbsent(
  spaces: KnowledgeSpace[] | null,
  space: KnowledgeSpace,
): Partial<BrainStore> {
  const existing = spaces ?? [];
  if (existing.some((s) => s.spaceId === space.spaceId)) {
    return {};
  }
  return { spaces: [...existing, { ...space, pages: space.pages ?? [] }] };
}

function adjustChildCount(
  pages: KnowledgePageMeta[],
  parentPageId: string | undefined,
  delta: number,
): KnowledgePageMeta[] {
  if (!parentPageId) {
    return pages;
  }
  return pages.map((p) =>
    p.pageId === parentPageId
      ? { ...p, childCount: Math.max(0, p.childCount + delta) }
      : p,
  );
}

function applySameSpaceMove(
  spaces: KnowledgeSpace[],
  spaceId: string,
  rootPageId: string,
  newRootParentPageId: string | undefined,
  subtreePages: KnowledgePageMeta[],
): KnowledgeSpace[] {
  const rootSnapshot = subtreePages.find((p) => p.pageId === rootPageId);
  const oldParentPageId = rootSnapshot?.parentPageId;

  return spaces.map((s) => {
    if (s.spaceId !== spaceId) {
      return s;
    }
    let pages = s.pages.map((p) =>
      p.pageId === rootPageId ? { ...p, parentPageId: newRootParentPageId } : p,
    );
    pages = adjustChildCount(pages, oldParentPageId, -1);
    pages = adjustChildCount(pages, newRootParentPageId, 1);
    return { ...s, pages, updatedAt: Date.now() };
  });
}

function applyCrossSpaceMove(
  spaces: KnowledgeSpace[],
  sourceSpaceId: string,
  targetSpaceId: string,
  rootPageId: string,
  newRootParentPageId: string | undefined,
  subtreeIds: Set<string>,
  subtreePages: KnowledgePageMeta[],
): KnowledgeSpace[] {
  const n = subtreeIds.size;
  const rootSnapshot = subtreePages.find((p) => p.pageId === rootPageId);
  const oldParentPageId = rootSnapshot?.parentPageId;

  return spaces.map((s) => {
    if (s.spaceId === sourceSpaceId) {
      let pages = s.pages.filter((p) => !subtreeIds.has(p.pageId));
      pages = adjustChildCount(pages, oldParentPageId, -1);
      return {
        ...s,
        pages,
        pageCount: Math.max(0, s.pageCount - n),
        updatedAt: Date.now(),
      };
    }
    if (s.spaceId === targetSpaceId) {
      const pagesWithSubtree: KnowledgePageMeta[] = [
        ...s.pages,
        ...subtreePages.map((p) =>
          p.pageId === rootPageId
            ? {
                ...p,
                spaceId: targetSpaceId,
                parentPageId: newRootParentPageId,
              }
            : { ...p, spaceId: targetSpaceId },
        ),
      ];
      const pages = adjustChildCount(pagesWithSubtree, newRootParentPageId, 1);
      return {
        ...s,
        pages,
        pageCount: s.pageCount + n,
        updatedAt: Date.now(),
      };
    }
    return s;
  });
}

// Replaces only the spaces of one ownerType, keeping the other slice + already-
// loaded page trees — a team switch swaps team spaces without refetching the
// cached user spaces.
function replaceSpacesOfOwnerType(
  state: BrainStore,
  ownerType: KnowledgeOwnerType,
  incoming: KnowledgeSpace[],
): Partial<BrainStore> {
  const admittedIncoming = incoming.filter(
    (s) => !state.removedSpaceIds.has(s.spaceId),
  );
  const prevById = new Map((state.spaces ?? []).map((s) => [s.spaceId, s]));
  const merged = admittedIncoming.map((s) => {
    const prev = prevById.get(s.spaceId);
    const keepTree = prev !== undefined && state.loadedSpaceIds.has(s.spaceId);
    return { ...s, pages: keepTree ? prev.pages : (s.pages ?? []) };
  });
  const kept = (state.spaces ?? []).filter((s) => s.ownerType !== ownerType);
  // keep the viewed space when it paginates out, else the deep-link re-resolves
  const incomingIds = new Set(admittedIncoming.map((s) => s.spaceId));
  const { viewedSpaceId, selectedPageId, pendingPageLocation } = state;
  const isViewed = (s: KnowledgeSpace) =>
    s.spaceId === viewedSpaceId ||
    s.spaceId === pendingPageLocation?.spaceId ||
    (selectedPageId != null &&
      s.pages.some((p) => p.pageId === selectedPageId));
  const preserved = (state.spaces ?? []).filter(
    (s) =>
      s.ownerType === ownerType && !incomingIds.has(s.spaceId) && isViewed(s),
  );
  const spaces = [...kept, ...merged, ...preserved];
  const spaceIds = new Set(spaces.map((s) => s.spaceId));
  return {
    spaces,
    loadedSpaceIds: new Set(
      [...state.loadedSpaceIds].filter((id) => spaceIds.has(id)),
    ),
    loadingSpaceIds: new Set(
      [...state.loadingSpaceIds].filter((id) => spaceIds.has(id)),
    ),
    spacePageErrors: Object.fromEntries(
      Object.entries(state.spacePageErrors).filter(([id]) => spaceIds.has(id)),
    ),
  };
}

export const createSidebarSlice: StateCreator<
  BrainStore,
  [],
  [],
  BrainSidebarSlice
> = (set, get) => ({
  spaces: INITIAL_BRAIN_STATE.spaces,
  isLoadingSpaces: INITIAL_BRAIN_STATE.isLoadingSpaces,
  isLoadingUserSpaces: INITIAL_BRAIN_STATE.isLoadingUserSpaces,
  isLoadingTeamSpaces: INITIAL_BRAIN_STATE.isLoadingTeamSpaces,
  hasLoadedUserSpaces: INITIAL_BRAIN_STATE.hasLoadedUserSpaces,
  hasLoadedTeamSpaces: INITIAL_BRAIN_STATE.hasLoadedTeamSpaces,
  loadError: INITIAL_BRAIN_STATE.loadError,
  expandedPageIds: INITIAL_BRAIN_STATE.expandedPageIds,
  expandedSpaceIds: INITIAL_BRAIN_STATE.expandedSpaceIds,
  expandedGroupTypes: INITIAL_BRAIN_STATE.expandedGroupTypes,
  selectedPageId: INITIAL_BRAIN_STATE.selectedPageId,
  activeMoveCount: INITIAL_BRAIN_STATE.activeMoveCount,
  loadedSpaceIds: INITIAL_BRAIN_STATE.loadedSpaceIds,
  loadingSpaceIds: INITIAL_BRAIN_STATE.loadingSpaceIds,
  spacePageErrors: INITIAL_BRAIN_STATE.spacePageErrors,
  pendingPageLocation: INITIAL_BRAIN_STATE.pendingPageLocation,
  pendingDeepLink: INITIAL_BRAIN_STATE.pendingDeepLink,
  viewedSpaceId: INITIAL_BRAIN_STATE.viewedSpaceId,
  removedPageIds: INITIAL_BRAIN_STATE.removedPageIds,
  removedSpaceIds: INITIAL_BRAIN_STATE.removedSpaceIds,
  userSpacesCursor: INITIAL_BRAIN_STATE.userSpacesCursor,
  teamSpacesCursor: INITIAL_BRAIN_STATE.teamSpacesCursor,
  userSpacesEpoch: INITIAL_BRAIN_STATE.userSpacesEpoch,
  teamSpacesEpoch: INITIAL_BRAIN_STATE.teamSpacesEpoch,
  userSpacesLoadingMoreEpoch: INITIAL_BRAIN_STATE.userSpacesLoadingMoreEpoch,
  teamSpacesLoadingMoreEpoch: INITIAL_BRAIN_STATE.teamSpacesLoadingMoreEpoch,

  setUserSpaces: (incoming, cursor) => {
    set((state) => ({
      ...replaceSpacesOfOwnerType(state, KnowledgeOwnerType.USER, incoming),
      userSpacesCursor: cursor,
    }));
  },

  setTeamSpaces: (incoming, cursor, teamId) => {
    set((state) => {
      const activeTeamId = useTeamStore.getState().activeTeamId;
      // A stale response can legitimately carry an empty page; comparing by
      // the request's origin teamId (not filtering spaces item-by-item) is
      // the only way to tell that apart from a genuinely empty active team.
      if (teamId !== activeTeamId) {
        log.debug(
          `[createSidebarSlice] discarding setTeamSpaces for teamId ${teamId} — active team is ${activeTeamId}`,
        );
        return {};
      }
      return {
        ...replaceSpacesOfOwnerType(state, KnowledgeOwnerType.TEAM, incoming),
        teamSpacesCursor: cursor,
      };
    });
  },

  setLoadingUserSpaces: (loading) => {
    set((state) => ({
      isLoadingUserSpaces: loading,
      hasLoadedUserSpaces: loading ? state.hasLoadedUserSpaces : true,
      isLoadingSpaces: loading || state.isLoadingTeamSpaces,
    }));
  },

  setLoadingTeamSpaces: (loading) => {
    set((state) => ({
      isLoadingTeamSpaces: loading,
      hasLoadedTeamSpaces: loading ? state.hasLoadedTeamSpaces : true,
      isLoadingSpaces: state.isLoadingUserSpaces || loading,
    }));
  },

  setLoadError: (error) => {
    set({ loadError: error });
  },

  addSpace: (space) => {
    set((state) => {
      if (state.removedSpaceIds.has(space.spaceId)) {
        return {};
      }
      const normalized: KnowledgeSpace = {
        ...space,
        pages: space.pages ?? [],
      };
      if (!state.spaces) {
        return { spaces: [normalized] };
      }
      const exists = state.spaces.some((s) => s.spaceId === space.spaceId);
      if (exists) {
        return {
          spaces: state.spaces.map((s) => {
            if (s.spaceId !== space.spaceId) {
              return s;
            }
            const preservedPages = state.loadedSpaceIds.has(space.spaceId)
              ? s.pages
              : normalized.pages;
            const merged = mergeDefinedFields(s, space);
            merged.pages = preservedPages;
            if (!('effectiveRole' in space)) {
              merged.effectiveRole = undefined;
            }
            return merged;
          }),
        };
      }
      return { spaces: [...state.spaces, normalized] };
    });
  },

  removeSpace: (spaceId, opts) => {
    set((state) => {
      const removed = state.spaces?.find((s) => s.spaceId === spaceId);
      const selectedInRemoved =
        state.selectedPageId != null &&
        (removed?.pages.some((p) => p.pageId === state.selectedPageId) ??
          false);
      const nextLoaded = new Set(state.loadedSpaceIds);
      nextLoaded.delete(spaceId);
      const nextLoading = new Set(state.loadingSpaceIds);
      nextLoading.delete(spaceId);
      const nextExpanded = new Set(state.expandedSpaceIds);
      nextExpanded.delete(spaceId);
      const nextSpacePageErrors = { ...state.spacePageErrors };
      delete nextSpacePageErrors[spaceId];
      const base: Partial<BrainStore> = {
        spaces: state.spaces
          ? state.spaces.filter((s) => s.spaceId !== spaceId)
          : null,
        viewedSpaceId:
          state.viewedSpaceId === spaceId ? null : state.viewedSpaceId,
        selectedPageId: selectedInRemoved ? null : state.selectedPageId,
        pendingPageLocation:
          state.pendingPageLocation?.spaceId === spaceId
            ? null
            : state.pendingPageLocation,
        loadedSpaceIds: nextLoaded,
        loadingSpaceIds: nextLoading,
        expandedSpaceIds: nextExpanded,
        spacePageErrors: nextSpacePageErrors,
      };
      if (!opts?.tombstone) {
        return base;
      }
      const removedSpaceIds = new Set(state.removedSpaceIds);
      removedSpaceIds.add(spaceId);
      return { ...base, removedSpaceIds };
    });
  },

  updateSpace: (space) => {
    set((state) => ({
      spaces: state.spaces
        ? state.spaces.map((s) => {
            if (s.spaceId !== space.spaceId) {
              return s;
            }
            const { pages: _ignored, ...meta } = space;
            const next = mergeDefinedFields(s, meta);
            if (!('effectiveRole' in space)) {
              next.effectiveRole = undefined;
            }
            return next;
          })
        : null,
    }));
  },

  replaceSpace: (oldSpaceId, space) => {
    set((state) => {
      if (!state.spaces) {
        return {};
      }
      // The stream INSERT for the real spaceId can land before the create
      // mutation resolves, so a duplicate entry may already sit alongside
      // the temp one this call is meant to replace — collapse both into one.
      // The temp entry can also be gone already (a spaces refresh ran in
      // between), so the duplicate — or nothing — may be all that's left;
      // the space must still end up present exactly once either way.
      const oldEntry = state.spaces.find((s) => s.spaceId === oldSpaceId);
      const duplicate = state.spaces.find(
        (s) => s.spaceId === space.spaceId && s.spaceId !== oldSpaceId,
      );
      const merged = mergeReplacedSpace(oldEntry, duplicate, space);

      if (oldEntry) {
        return {
          spaces: state.spaces
            .filter(
              (s) => s.spaceId === oldSpaceId || s.spaceId !== space.spaceId,
            )
            .map((s) => (s.spaceId === oldSpaceId ? merged : s)),
        };
      }
      if (duplicate) {
        return {
          spaces: state.spaces.map((s) =>
            s.spaceId === space.spaceId ? merged : s,
          ),
        };
      }
      return { spaces: [...state.spaces, merged] };
    });
  },

  addPageToSpace: (spaceId, page) => {
    set((state) => {
      if (!state.spaces) {
        return {};
      }
      return {
        spaces: state.spaces.map((s) => {
          if (s.spaceId !== spaceId) {
            return s;
          }
          const exists = s.pages.some((p) => p.pageId === page.pageId);
          if (exists) {
            return {
              ...s,
              pages: s.pages.map((p) => (p.pageId === page.pageId ? page : p)),
            };
          }
          return {
            ...s,
            pages: [...s.pages, page],
            pageCount: s.pageCount + 1,
            updatedAt: Date.now(),
          };
        }),
      };
    });
  },

  removePageFromSpace: (spaceId, pageId) => {
    set((state) => {
      const removedPageIds = new Set(state.removedPageIds);
      removedPageIds.add(pageId);
      if (!state.spaces) {
        return { removedPageIds };
      }
      return {
        spaces: state.spaces.map((s) =>
          s.spaceId === spaceId
            ? {
                ...s,
                pages: s.pages.filter((p) => p.pageId !== pageId),
                pageCount: Math.max(0, s.pageCount - 1),
              }
            : s,
        ),
        removedPageIds,
      };
    });
  },

  replacePageInSpace: (spaceId, oldPageId, page) => {
    set((state) => {
      if (!state.spaces) {
        return {};
      }
      return {
        spaces: state.spaces.map((s) =>
          s.spaceId === spaceId
            ? {
                ...s,
                pages: s.pages.map((p) => (p.pageId === oldPageId ? page : p)),
              }
            : s,
        ),
      };
    });
  },

  updatePageInSpace: (spaceId, page) => {
    set((state) => {
      const favoritePages = state.favoritePages.map((p) =>
        p.pageId === page.pageId ? page : p,
      );
      const recentPages = state.recentPages.map((p) =>
        p.pageId === page.pageId ? page : p,
      );
      if (!state.spaces) {
        return { favoritePages, recentPages };
      }
      return {
        spaces: state.spaces.map((s) =>
          s.spaceId === spaceId
            ? {
                ...s,
                pages: s.pages.map((p) =>
                  p.pageId === page.pageId ? page : p,
                ),
              }
            : s,
        ),
        favoritePages,
        recentPages,
      };
    });
  },

  toggleExpandPage: (pageId) => {
    set((state) => {
      const next = new Set(state.expandedPageIds);
      if (next.has(pageId)) {
        next.delete(pageId);
      } else {
        next.add(pageId);
      }
      return { expandedPageIds: next };
    });
  },

  expandPage: (pageId) => {
    set((state) => {
      const next = new Set(state.expandedPageIds);
      next.add(pageId);
      return { expandedPageIds: next };
    });
  },

  collapsePage: (pageId) => {
    set((state) => {
      const next = new Set(state.expandedPageIds);
      next.delete(pageId);
      return { expandedPageIds: next };
    });
  },

  toggleExpandSpace: (spaceId) => {
    set((state) => {
      const next = new Set(state.expandedSpaceIds);
      if (next.has(spaceId)) {
        next.delete(spaceId);
      } else {
        next.add(spaceId);
      }
      return { expandedSpaceIds: next };
    });
  },

  toggleExpandGroup: (ownerType: BrainSpaceGroupKey) => {
    set((state) => {
      const next = new Set(state.expandedGroupTypes);
      if (next.has(ownerType)) {
        next.delete(ownerType);
      } else {
        next.add(ownerType);
      }
      return { expandedGroupTypes: next };
    });
  },

  expandGroup: (key) => {
    set((state) => {
      const next = new Set(state.expandedGroupTypes);
      next.add(key);
      return { expandedGroupTypes: next };
    });
  },

  selectPage: (pageId) => {
    set({ selectedPageId: pageId });
  },

  resetBrain: () => {
    set({
      ...INITIAL_BRAIN_STATE,
      _hasHydrated: true,
      expandedPageIds: new Set<string>(),
      expandedSpaceIds: new Set<string>(),
      expandedGroupTypes: new Set<BrainSpaceGroupKey>(),
      loadedSpaceIds: new Set<string>(),
      loadingSpaceIds: new Set<string>(),
      spacePageErrors: {},
      pendingPageLocation: null,
      pendingDeepLink: null,
      viewedSpaceId: null,
      removedPageIds: new Set<string>(),
      removedSpaceIds: new Set<string>(),
      isLoadingUserSpaces: false,
      isLoadingTeamSpaces: false,
      hasLoadedUserSpaces: false,
      hasLoadedTeamSpaces: false,
    });
  },

  moveSubtreeBetweenSpaces: (input: MoveSubtreeBetweenSpacesInput) => {
    const {
      sourceSpaceId,
      targetSpaceId,
      subtreePages,
      rootPageId,
      newRootParentPageId,
    } = input;
    const subtreeIds = new Set(subtreePages.map((p) => p.pageId));

    set((state) => {
      if (!state.spaces) {
        return {};
      }
      if (sourceSpaceId === targetSpaceId) {
        return {
          spaces: applySameSpaceMove(
            state.spaces,
            sourceSpaceId,
            rootPageId,
            newRootParentPageId,
            subtreePages,
          ),
        };
      }
      return {
        spaces: applyCrossSpaceMove(
          state.spaces,
          sourceSpaceId,
          targetSpaceId,
          rootPageId,
          newRootParentPageId,
          subtreeIds,
          subtreePages,
        ),
      };
    });
  },

  snapshotSubtree: (
    spaceId: string,
    rootPageId: string,
  ): KnowledgePageMeta[] => {
    const space = get().spaces?.find((s) => s.spaceId === spaceId);
    if (!space) {
      return [];
    }
    const descendantIds = collectDescendantPageIds(space.pages, rootPageId);
    return space.pages.filter((p) => descendantIds.has(p.pageId));
  },

  incrementActiveMoveCount: () => {
    set((s) => ({ activeMoveCount: s.activeMoveCount + 1 }));
  },

  decrementActiveMoveCount: () => {
    set((s) => ({ activeMoveCount: Math.max(0, s.activeMoveCount - 1) }));
  },

  setSpacePages: (spaceId, pages) => {
    set((state) => {
      const nextLoaded = new Set(state.loadedSpaceIds);
      nextLoaded.add(spaceId);
      const nextLoading = new Set(state.loadingSpaceIds);
      nextLoading.delete(spaceId);
      const nextErrors = { ...state.spacePageErrors };
      delete nextErrors[spaceId];
      const spaces = state.spaces
        ? state.spaces.map((s) => (s.spaceId === spaceId ? { ...s, pages } : s))
        : state.spaces;
      return {
        spaces,
        loadedSpaceIds: nextLoaded,
        loadingSpaceIds: nextLoading,
        spacePageErrors: nextErrors,
      };
    });
  },

  setSpacePagesLoading: (spaceId, loading) => {
    set((state) => {
      const next = new Set(state.loadingSpaceIds);
      if (loading) {
        next.add(spaceId);
        const nextErrors = { ...state.spacePageErrors };
        delete nextErrors[spaceId];
        return { loadingSpaceIds: next, spacePageErrors: nextErrors };
      }
      next.delete(spaceId);
      return { loadingSpaceIds: next };
    });
  },

  setSpacePagesError: (spaceId, error) => {
    set((state) => {
      const nextLoading = new Set(state.loadingSpaceIds);
      nextLoading.delete(spaceId);
      return {
        spacePageErrors: { ...state.spacePageErrors, [spaceId]: error },
        loadingSpaceIds: nextLoading,
      };
    });
  },

  appendSpaces: (ownerType, spaces, cursor, teamId) => {
    set((state) => {
      if (ownerType === KnowledgeOwnerType.TEAM) {
        const activeTeamId = useTeamStore.getState().activeTeamId;
        // Same rationale as setTeamSpaces: compare the request's origin
        // teamId, don't filter spaces item-by-item.
        if (teamId !== activeTeamId) {
          log.debug(
            `[createSidebarSlice] discarding appendSpaces(TEAM) for teamId ${teamId} — active team is ${activeTeamId}`,
          );
          return {};
        }
      }
      const existing = state.spaces ?? [];
      const existingIds = new Set(existing.map((s) => s.spaceId));
      const incoming = spaces
        .filter((s) => !existingIds.has(s.spaceId))
        .filter((s) => !state.removedSpaceIds.has(s.spaceId))
        .map((s) => ({ ...s, pages: s.pages ?? [] }));
      const cursorField =
        ownerType === KnowledgeOwnerType.USER
          ? 'userSpacesCursor'
          : 'teamSpacesCursor';
      return {
        spaces: [...existing, ...incoming],
        [cursorField]: cursor,
      };
    });
  },

  bumpSpacesEpoch: (ownerType) => {
    const state = get();
    const next =
      (ownerType === KnowledgeOwnerType.USER
        ? state.userSpacesEpoch
        : state.teamSpacesEpoch) + 1;
    set(
      ownerType === KnowledgeOwnerType.USER
        ? { userSpacesEpoch: next }
        : { teamSpacesEpoch: next },
    );
    return next;
  },

  claimLoadingMoreSpaces: (ownerType, epoch) => {
    const state = get();
    const current =
      ownerType === KnowledgeOwnerType.USER
        ? state.userSpacesLoadingMoreEpoch
        : state.teamSpacesLoadingMoreEpoch;
    if (current === epoch) {
      return false;
    }
    set(
      ownerType === KnowledgeOwnerType.USER
        ? { userSpacesLoadingMoreEpoch: epoch }
        : { teamSpacesLoadingMoreEpoch: epoch },
    );
    return true;
  },

  releaseLoadingMoreSpaces: (ownerType, epoch) => {
    const state = get();
    const current =
      ownerType === KnowledgeOwnerType.USER
        ? state.userSpacesLoadingMoreEpoch
        : state.teamSpacesLoadingMoreEpoch;
    if (current !== epoch) {
      return;
    }
    set(
      ownerType === KnowledgeOwnerType.USER
        ? { userSpacesLoadingMoreEpoch: null }
        : { teamSpacesLoadingMoreEpoch: null },
    );
  },

  resetTeamSpacesCursor: () => {
    set({ teamSpacesCursor: null });
  },

  ensureSpacePresent: (space) => {
    set((state) =>
      state.removedSpaceIds.has(space.spaceId)
        ? {}
        : appendSpaceIfAbsent(state.spaces, space),
    );
  },

  restoreSpace: (space) => {
    set((state) => {
      const removedSpaceIds = new Set(state.removedSpaceIds);
      removedSpaceIds.delete(space.spaceId);
      return { removedSpaceIds, ...appendSpaceIfAbsent(state.spaces, space) };
    });
  },

  setPendingPageLocation: (loc: PendingPageLocation | null) => {
    set({ pendingPageLocation: loc });
  },

  setPendingDeepLink: (id: string | null) => {
    set({ pendingDeepLink: id });
  },

  setViewedSpaceId: (id: string | null) => {
    set({ viewedSpaceId: id });
  },

  bumpSpacePageCount: (spaceId, delta) => {
    set((state) => {
      if (!state.spaces) {
        return {};
      }
      return {
        spaces: state.spaces.map((s) =>
          s.spaceId === spaceId
            ? { ...s, pageCount: Math.max(0, s.pageCount + delta) }
            : s,
        ),
      };
    });
  },
});
